import React from "react";
import { Navigate, Route } from "react-router-dom";
import { useSelector } from "react-redux";

import { useProfile } from "../Hooks/UserHooks";
import usePermission from "../Hooks/usePermission";

/**
 * AuthProtected component that handles both authentication and authorization.
 *
 * Props:
 * - children: The component to render if access is granted
 * - permission: Single permission required (e.g., 'asset:read')
 * - permissions: Array of permissions (used with operator)
 * - operator: 'any' (default) or 'all' for permission combination
 * - adminOnly: If true, only admins can access
 * - superAdminOnly: If true, only super admins can access
 * - fallbackPath: Where to redirect if permission denied (default: '/dashboard')
 * - requireActiveOrg: If true (default), redirect users in Global View to the
 *   global dashboard. Set to false on routes that are intentionally Global
 *   View (e.g. /app/global/*) so they remain reachable while in that context.
 */
const AuthProtected = ({
  children,
  permission,
  permissions,
  operator = 'any',
  adminOnly = false,
  superAdminOnly = false,
  fallbackPath = '/pages-404',
  requireActiveOrg = true,
  ...props
}) => {
  const { userProfile, loading } = useProfile();
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    isAdmin,
  } = usePermission();
  const isGlobalView = useSelector((s) => s.ActiveContext?.isGlobalView);

  // Check authentication first
  if (!userProfile && loading) {
    return (
      <Navigate to={{ pathname: "/login", state: { from: props.location } }} />
    );
  }

  // Check org approval status - redirect to pending-approval if org is PENDING
  const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const orgStatus = authUser?.orgApprovalStatus;
  const isPendingApprovalPage = window.location.pathname === '/pending-approval';
  if (orgStatus === 'PENDING' && !isPendingApprovalPage) {
    return <Navigate to="/pending-approval" replace />;
  }

  // Active-org gate: routes that operate against a specific organization
  // require the user to be in an org context. When the user is in Global
  // View, send them to the global dashboard instead of letting org-scoped
  // pages render with an empty/forbidden state. Pass requireActiveOrg=false
  // on Global-View pages to opt out.
  if (requireActiveOrg && isGlobalView) {
    return <Navigate to="/app/global/dashboard" replace />;
  }

  // If no permission requirements, just render children (authenticated only)
  const hasPermissionRequirements = permission || permissions || adminOnly || superAdminOnly;
  if (!hasPermissionRequirements) {
    return <>{children}</>;
  }

  // Check authorization
  const checkAccess = () => {
    // Super admin bypass (unless checking specifically for super admin)
    if (!superAdminOnly && isSuperAdmin) {
      return true;
    }

    // Super admin only check
    if (superAdminOnly) {
      return isSuperAdmin;
    }

    // Admin only check
    if (adminOnly) {
      return isAdmin;
    }

    // Single permission check
    if (permission) {
      return hasPermission(permission);
    }

    // Multiple permissions check
    if (permissions && permissions.length > 0) {
      return operator === 'all'
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
    }

    return false;
  };

  if (checkAccess()) {
    return <>{children}</>;
  }

  // Permission denied - redirect to fallback
  return <Navigate to={{ pathname: fallbackPath }} replace />;
};

const AccessRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => { return ( <> <Component {...props} /> </> );
      }}
    />
  );
};

export { AuthProtected, AccessRoute };



